import { ButtonBase, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useBrowsableCategories } from '@/hooks/use-category-browser.ts';

import { getColorWithOpacity } from '@/utils/colorHelpers.js';
import { ALL_CATEGORIES_KEY, APP_HEADER_HEIGHT_VAR } from '@/utils/constants.js';

import { CategoryCard } from './category-card.tsx';
import { SearchStepHeader } from './search-step-header.tsx';
import { TreatmentTypeCard } from './treatment-type-card.tsx';
import { TreatmentTypeGroup } from './treatment-type-group.tsx';

const useStyles = makeStyles((theme: any) => ({
    container: {
        width: '100%',
        boxSizing: 'border-box',
        scrollMarginTop: `calc(var(${APP_HEADER_HEIGHT_VAR}, 0px) + 1rem)`,
    },
    categoryGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: '0.75rem',
        marginTop: '1.125rem',
        alignItems: 'stretch',
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: '1fr',
        },
    },
    orDivider: {
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        marginTop: '1.625rem',
    },
    dividerLine: {
        flex: 1,
        height: 1,
        backgroundColor: theme.palette.grey[300],
    },
    orLabel: {
        ...theme.typography.body2,
        color: theme.palette.common.darkGrey,
        opacity: 0.5,
        margin: 0,
    },
    allRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: '0.75rem',
        marginTop: '1.125rem',
        '& > *': {
            gridColumn: 2,
        },
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: '1fr',
            '& > *': {
                gridColumn: 'auto',
            },
        },
    },
    stepTwo: {
        scrollMarginTop: `calc(var(${APP_HEADER_HEIGHT_VAR}, 0px) + 1rem)`,
        marginTop: '1.75rem',
        paddingTop: '1.25rem',
        borderTop: theme.border.light,
    },
    stepTwoTitle: {
        ...theme.typography.HL1,
        color: theme.palette.common.darkGrey,
        margin: '1rem 0 0',
    },
    treatmentGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.875rem',
        marginTop: '1.125rem',
        alignItems: 'stretch',
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: '1fr',
        },
    },
    changeCategoryBar: {
        position: 'sticky',
        bottom: 0,
        zIndex: 30,
        display: 'flex',
        justifyContent: 'flex-start',
        padding: '1rem 0 1.375rem',
        background: `linear-gradient(to top, ${theme.palette.common.cream} 55%, ${getColorWithOpacity(theme.palette.common.cream, 0)})`,
    },
    changeCategoryButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.8125rem 1.125rem',
        border: `1px solid ${theme.palette.common.yellow}`,
        borderRadius: theme.radius.field,
        backgroundColor: theme.palette.common.white,
        color: theme.palette.common.darkGrey,
        boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)',
        cursor: 'pointer',
        transition: 'border-color 200ms ease',
        '&:focus-visible': {
            outline: `2px solid ${theme.palette.common.darkGrey}`,
            outlineOffset: -4,
        },
    },
}));

export const CategoryBrowser = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { categories, isSearchActive, selectedLocation } = useBrowsableCategories();
    const [selectedCategoryKey, setSelectedCategoryKey] = useState<string | null>(null);
    const [selectionContext, setSelectionContext] = useState<string | null>(selectedLocation);
    const containerRef = useRef<HTMLDivElement>(null);
    const treatmentResultsRef = useRef<HTMLDivElement>(null);

    const hasSearchResults = isSearchActive && categories.length > 0;

    useEffect(() => {
        if (!hasSearchResults) return;

        treatmentResultsRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }, [hasSearchResults]);

    if (selectionContext !== selectedLocation) {
        setSelectionContext(selectedLocation);
        setSelectedCategoryKey(null);
    }

    if (categories.length === 0) return null;

    const selectionStillVisible =
        selectedCategoryKey === ALL_CATEGORIES_KEY ||
        categories.some((category) => category.key === selectedCategoryKey);
    const activeSelection = selectionStillVisible ? selectedCategoryKey : null;
    const effectiveSelection = isSearchActive ? ALL_CATEGORIES_KEY : activeSelection;
    const isAllSelected = effectiveSelection === ALL_CATEGORIES_KEY;
    const selectedCategory = categories.find((category) => category.key === effectiveSelection);

    const handleCategorySelect = (categoryKey: string): void => {
        setSelectedCategoryKey(categoryKey);
        requestAnimationFrame(() => {
            treatmentResultsRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
        });
    };

    const backToCategories = (): void => {
        setSelectedCategoryKey(null);
        requestAnimationFrame(() => {
            containerRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
            containerRef.current?.querySelector<HTMLButtonElement>('button')?.focus({ preventScroll: true });
        });
    };

    return (
        <div className={classes.container} ref={containerRef}>
            <SearchStepHeader step={1} title={t('ChooseCategoryStepTitle')} />
            <div className={classes.categoryGrid}>
                {categories.map((category) => (
                    <CategoryCard
                        key={category.key}
                        name={category.name}
                        isSelected={category.key === effectiveSelection}
                        onSelect={() => handleCategorySelect(category.key)}
                        dataTestId={`category-browser-category-${category.key}`}
                    />
                ))}
            </div>
            <div className={classes.orDivider}>
                <span className={classes.dividerLine} />
                <Typography className={classes.orLabel} component="span">
                    {t('CategoryBrowserOr')}
                </Typography>
                <span className={classes.dividerLine} />
            </div>
            <div className={classes.allRow}>
                <CategoryCard
                    name={t('SeeAllTreatmentTypesCta')}
                    isSelected={isAllSelected}
                    onSelect={() => handleCategorySelect(ALL_CATEGORIES_KEY)}
                    dataTestId="category-browser-category-all"
                />
            </div>
            {effectiveSelection !== null && (
                <div className={classes.stepTwo} ref={treatmentResultsRef}>
                    <SearchStepHeader step={2} title={t('ChooseTreatmentTypeStepTitle')} />
                    <Typography className={classes.stepTwoTitle} component="h3">
                        {isAllSelected ? t('AllTreatmentTypes') : selectedCategory?.name}
                    </Typography>
                    {isAllSelected ? (
                        categories.map((category) => (
                            <TreatmentTypeGroup
                                key={category.key}
                                name={category.name}
                                treatments={category.treatments}
                            />
                        ))
                    ) : (
                        <div className={classes.treatmentGrid}>
                            {selectedCategory?.treatments.map((treatment) => (
                                <TreatmentTypeCard
                                    key={treatment.rowKey}
                                    name={treatment.name}
                                    isOnline={treatment.isOnline}
                                    isSelected={treatment.isSelected}
                                    meta={treatment.meta}
                                    onSelect={treatment.select}
                                    dataTestId={`category-browser-treatment-${treatment.rowKey}`}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
            {activeSelection !== null && (
                <div className={classes.changeCategoryBar}>
                    <ButtonBase className={classes.changeCategoryButton} onClick={backToCategories}>
                        <span aria-hidden="true">‹</span>
                        <span>{t('ChangeCategoryCta')}</span>
                    </ButtonBase>
                </div>
            )}
        </div>
    );
};
